import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { scrollBarStyles } from '@/utils/theme';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import { ColorResetButton } from '../VariationFour/CoverBox';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationsStyles';

const DEFAULT_FONT_SIZE = 62;
const DEFAULT_COLOR = '#FAC514';

export interface ImageVariation {
  leftImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightImageSrc: string;
  rightWatermarkTag: string;
  rightWatermarkPosition: string;
  bottomImageSrc: string;
  bottomWatermarkTag: string;
  bottomWatermarkPosition: string;
  watermark: boolean;
  bottomText: string;
  bottomTextBrakes?: boolean;
  bottomFontSize?: number;
  bottomTextColor: string;
}

const ImagesBox = styled.div<{
  $hasImages?: boolean;
  $hasLeftBorder?: boolean;
}>`
  padding: 24px;
  ${({ $hasImages }) =>
    $hasImages &&
    css`
      padding-bottom: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
  ${({ $hasLeftBorder }) =>
    $hasLeftBorder &&
    css`
      border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
`;

const StyledUploadedImages = styled(UploadedImages)`
  max-height: 390px;
  min-height: 200px;
`;

const TextAreaBox = styled.div`
  padding: 24px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  flex-grow: 1;
  align-content: start;
`;

const TextItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LabelHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CounterBadge = styled(Text)`
  background: ${({ theme }) => theme.colors.purple};
  width: 20px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AdditionalBox = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const AdditionalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StyledRemoveButton = styled(RemoveButton)`
  background: transparent;
  height: auto;
  padding: 0;
  margin-bottom: 8px;
  text-decoration: underline;
  transition: all 0.2s;
  &:hover {
    border-color: transparent;
    opacity: 0.8;
  }
`;

const BottomTextArea = styled(Textarea)`
  height: 68px;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const AddMoreHolder = styled.div`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const StyeldAddMoreButton = styled(AddMoreButton)`
  margin-left: auto;
`;

const StyledTextAreaBox = styled(TextAreaBox)`
  max-height: 437px;
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  ${scrollBarStyles}
`;

const TextLeft = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const ColorInputHolder = styled.div`
  flex: 1;
`;

const ColorInput = styled(Input)`
  padding: 4px 8px;
  height: 46px;
  width: 100%;
`;

const ColorHolder = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  width: 100%;
`;

const ColorHolderWrapper = styled.div`
  max-width: 440px;
`;

type BottomTextItem = {
  text: string;
  brakes: boolean;
  fontSize?: number;
};

interface Props {
  inArticle?: boolean;
}

const VariationMainEditorialTemplate: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);
  const [bottomImages, setBottomImages] = useState<ImageData[]>([]);

  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [showLeftImagesModal, setShowLeftImagesModal] = useState(false);
  const [showRightImagesModal, setShowRightImagesModal] = useState(false);
  const [showBottomImagesModal, setShowBottomImagesModal] = useState(false);

  const [watermark, setWatermark] = useState(true);
  const [multipleVariations, setMultipleVariations] = useState(false);
  const [coverFrameColor, setCoverFrameColor] = useState<string>(DEFAULT_COLOR);
  const [bottomTexts, setBottomTexts] = useState<BottomTextItem[]>([
    {
      text: '',
      brakes: false,
      fontSize: DEFAULT_FONT_SIZE,
    },
  ]);

  const generateVariations = useCallback(() => {
    if (!leftImages.length || !rightImages.length) return;

    const bottomItems = bottomTexts.filter(Boolean);

    const baseCombos = leftImages.flatMap((leftImage) =>
      rightImages.flatMap((rightImage) => {
        const bottoms = bottomImages;

        return bottoms.map((bottomImage) => ({
          leftWatermarkTag: getWatermarkTag(leftImage),
          leftWatermarkPosition: getWatermarkPosition(leftImage),
          leftImageSrc:
            leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
          rightWatermarkTag: getWatermarkTag(rightImage),
          rightWatermarkPosition: getWatermarkPosition(rightImage),
          rightImageSrc:
            rightImage.croppedImage ?? URL.createObjectURL(rightImage.image),
          bottomWatermarkTag: getWatermarkTag(bottomImage),
          bottomWatermarkPosition: getWatermarkPosition(bottomImage),
          bottomImageSrc:
            bottomImage.croppedImage ?? URL.createObjectURL(bottomImage.image),
        }));
      }),
    );

    const defaultBottom = [
      {
        text: '',
        brakes: false,
        fontSize: DEFAULT_FONT_SIZE,
        bottomTextColor: DEFAULT_COLOR,
      },
    ];

    const newVariations: ImageVariation[] = baseCombos.flatMap((combo) =>
      (bottomItems.length ? bottomItems : defaultBottom).map((b) => ({
        ...combo,
        watermark,
        bottomText: b?.text ?? '',
        bottomTextBrakes: !!b?.brakes,
        bottomFontSize: b?.fontSize ?? DEFAULT_FONT_SIZE,
        bottomTextColor: coverFrameColor ?? DEFAULT_COLOR,
      })),
    );

    setVariations(newVariations);
  }, [
    leftImages,
    rightImages,
    bottomImages,
    bottomTexts,
    watermark,
    coverFrameColor,
  ]);

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      type="vertical"
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const renderRightImages = rightImages.map((image, index) => (
    <UploadedImageRow
      type="vertical"
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setRightImages}
    />
  ));

  const renderBottomImages = bottomImages.map((image, index) => (
    <UploadedImageRow
      type="horizontal"
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setBottomImages}
    />
  ));

  const addBottomText = () =>
    setBottomTexts((prev) => [
      ...prev,
      {
        text: '',
        brakes: false,
        fontSize: DEFAULT_FONT_SIZE,
      },
    ]);

  const updateBottomText = (idx: number, value: string) =>
    setBottomTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, text: value } : t)),
    );

  const toggleBottomTextFlag = (
    idx: number,
    key: keyof Pick<BottomTextItem, 'brakes'>,
  ) => {
    setBottomTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [key]: !t[key] } : t)),
    );
  };

  const removeBottomText = (idx: number) =>
    setBottomTexts((prev) => prev.filter((_, i) => i !== idx));

  const updateBottomFontSize = (idx: number, size: string) =>
    setBottomTexts((prev) =>
      prev.map((b, i) =>
        i === idx
          ? {
              ...b,
              fontSize: size === '' ? undefined : parseInt(size) || undefined,
            }
          : b,
      ),
    );

  const onReset = () => {
    setRightImages([]);
    setLeftImages([]);
    setBottomImages([]);
    setVariations([]);
    setImages([]);
    setCoverFrameColor(DEFAULT_COLOR);
    setBottomTexts([
      {
        text: '',
        brakes: false,
        fontSize: DEFAULT_FONT_SIZE,
      },
    ]);
    setWatermark(true);
  };

  const isResetDisabled =
    !leftImages?.length &&
    !rightImages?.length &&
    !bottomImages?.length &&
    !bottomTexts.length &&
    !watermark;

  return (
    <>
      <VariationsStyles />
      <Box>
        <Header>
          <div>
            <Heading $type="button">Main Editorial Template</Heading>
            <Text $type="smallHeadline" $color="gray">
              Two side-by-side cover images, one bottom image and a single text
              block
            </Text>
          </div>
          <HeaderButtons>
            <FancySwitch
              isActive={multipleVariations}
              onClick={() => setMultipleVariations((prev) => !prev)}
              text="Multiple Variations:"
            />
            <ResetButton
              color="red"
              onClick={onReset}
              disabled={isResetDisabled}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <div>
            <StyledTextAreaBox>
              {bottomTexts.map((item, idx) => (
                <TextItem key={idx}>
                  <div>
                    <LabelHolder>
                      <LabelText $type="smallHeadline">
                        Text
                        {bottomTexts.length > 1 && (
                          <CounterBadge $type="extraSmall" $color="white">
                            {idx + 1}
                          </CounterBadge>
                        )}
                      </LabelText>
                      {bottomTexts.length > 1 && (
                        <StyledRemoveButton
                          color="red"
                          onClick={() => removeBottomText(idx)}
                        >
                          Remove
                        </StyledRemoveButton>
                      )}
                    </LabelHolder>
                    <BottomTextArea
                      onChange={(e) => updateBottomText(idx, e.target.value)}
                      value={item.text}
                      placeholder="Add text"
                    />
                  </div>
                  <div>
                    <LabelText $type="smallHeadline">Font Size, px</LabelText>
                    <Input
                      type="number"
                      min="0"
                      value={item.fontSize ?? ''}
                      onChange={(e) =>
                        updateBottomFontSize(idx, e.target.value)
                      }
                      placeholder="Add Font Size"
                    />
                  </div>
                  <TextLeft>
                    <Checkbox
                      checked={!!item.brakes}
                      onChange={() => toggleBottomTextFlag(idx, 'brakes')}
                    >
                      Custom brakes
                    </Checkbox>
                  </TextLeft>
                </TextItem>
              ))}
            </StyledTextAreaBox>
            {multipleVariations && (
              <AddMoreHolder>
                {bottomTexts.length > 1 && (
                  <AttachedCountBadge count={bottomTexts.length} text="Texts" />
                )}
                <StyeldAddMoreButton color="colorized" onClick={addBottomText}>
                  Add text
                </StyeldAddMoreButton>
              </AddMoreHolder>
            )}
            <ImageGrid>
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">Left Cover Image</LabelText>
                {!!leftImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderLeftImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowLeftImagesModal(true)}
                        color="colorized"
                      >
                        Add more left images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <UploadMultipleImages
                    coverIndex={1}
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
              <ImagesBox $hasLeftBorder $hasImages={!!rightImages?.length}>
                <LabelText $type="smallHeadline">Right Cover Image</LabelText>
                {!!rightImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderRightImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={rightImages.length} />
                      <AddMoreButton
                        onClick={() => setShowRightImagesModal(true)}
                        color="colorized"
                      >
                        Add more right images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <UploadMultipleImages
                    coverIndex={2}
                    setImages={setRightImages}
                  />
                )}
              </ImagesBox>
            </ImageGrid>
            <ImagesBox $hasImages={!!bottomImages?.length}>
              <LabelText $type="smallHeadline">Bottom Cover Image</LabelText>
              {!!bottomImages?.length ? (
                <>
                  <StyledUploadedImages>
                    {renderBottomImages}
                  </StyledUploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={bottomImages.length} />
                    <AddMoreButton
                      onClick={() => setShowBottomImagesModal(true)}
                      color="colorized"
                    >
                      Add more bottom images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <UploadMultipleImages
                  coverIndex={1}
                  setImages={setBottomImages}
                />
              )}
            </ImagesBox>
            <AdditionalBox>
              <Text $type="smallHeadline" $color="gray">
                Additional:
              </Text>
              <AdditionalRow>
                <Checkbox
                  checked={!!watermark}
                  onChange={() => setWatermark((prev) => !prev)}
                >
                  Bored Panda Logo
                </Checkbox>
                <ColorHolderWrapper>
                  <ColorHolder>
                    <ColorInputHolder>
                      <LabelText $type="smallHeadline">
                        Text & Sticking Out Background Color
                      </LabelText>
                      <ColorInput
                        type="color"
                        value={coverFrameColor}
                        onChange={(e) => setCoverFrameColor(e.target.value)}
                      />
                    </ColorInputHolder>
                    <ColorResetButton
                      onClick={() => setCoverFrameColor(DEFAULT_COLOR)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M7.48438 9.34766H2.98438V4.84766"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.16635 17.8336C7.32014 18.9874 8.79015 19.7732 10.3905 20.0915C11.9908 20.4098 13.6496 20.2464 15.1571 19.622C16.6646 18.9976 17.9531 17.9402 18.8596 16.5835C19.7661 15.2268 20.25 13.6317 20.25 12C20.25 10.3683 19.7661 8.77325 18.8596 7.41655C17.9531 6.05984 16.6646 5.00242 15.1571 4.378C13.6496 3.75357 11.9908 3.5902 10.3905 3.90853C8.79015 4.22685 7.32014 5.01259 6.16635 6.16637L2.98438 9.34835"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </ColorResetButton>
                  </ColorHolder>
                </ColorHolderWrapper>
              </AdditionalRow>
            </AdditionalBox>
          </div>
        )}
      </Box>
      <Filler fontFamily="SourceSansProBold" fontWeight="bold" />
      {showContent && (
        <ButtonHolder>
          <Button
            disabled={
              !leftImages?.length ||
              !rightImages?.length ||
              !bottomImages?.length
            }
            onClick={generateVariations}
          >
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showLeftImagesModal && (
          <MoreImagesModal
            onClose={() => setShowLeftImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={2}
          />
        )}
        {!!showBottomImagesModal && (
          <MoreImagesModal
            onClose={() => setShowBottomImagesModal(false)}
            setImages={setBottomImages}
            coverIndex={3}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationMainEditorialTemplate;
